import { FC, PropsWithChildren } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import {
  PaidDataInput,
  StreamCategory,
  StreamType,
} from '@/__generated__/graphql.ts';
import { CustomTargetsData } from '@/__generated__/schema-types.ts';
import { buildPaidDataInput, PaidDataFormValues } from '@/utils/paid-data.ts';
import { StreamPricingType } from '../stream-pricing-type';

export type StreamFormInputs = {
  name: string;
  descriptionJson: string;
  label: string;
  streamCategory: StreamCategory;
  paidData?: PaidDataFormValues | null;
  packageOnly?: boolean;
  apiAccessEnabled: boolean;
  streamType?: StreamType;
  customTargetsData?: CustomTargetsData | null;
};

export type StreamFormSubmitValues = Omit<StreamFormInputs, 'paidData'> & {
  paidData?: PaidDataInput;
};

interface StreamFormProps {
  onSubmit: (input: StreamFormSubmitValues) => Promise<void>;
  pricingType: StreamPricingType;
  initialValues?: StreamFormInputs;
  customStream?: boolean;
}

export const StreamForm: FC<PropsWithChildren<StreamFormProps>> = ({
  onSubmit,
  pricingType,
  initialValues,
  customStream,
  children,
}) => {
  const isPaid = pricingType === StreamPricingType.Paid;

  const formMethods = useForm<StreamFormInputs>({
    mode: 'onChange',
    defaultValues: initialValues
      ? {
          name: initialValues.name,
          descriptionJson: initialValues.descriptionJson,
          label: initialValues.label,
          streamCategory: initialValues.streamCategory,
          paidData: initialValues.paidData ?? undefined,
          packageOnly: initialValues.packageOnly,
          apiAccessEnabled: initialValues.apiAccessEnabled,
          streamType: initialValues.streamType,
          customTargetsData: initialValues.customTargetsData ?? undefined,
        }
      : customStream
        ? {
            packageOnly: false,
            apiAccessEnabled: true,
          }
        : { apiAccessEnabled: true },
  });

  const submit: SubmitHandler<StreamFormInputs> = async (
    data: StreamFormInputs,
  ) => {
    const packageOnly = initialValues
      ? data.packageOnly
      : pricingType === StreamPricingType.PackageOnly;

    const input: StreamFormSubmitValues = {
      name: data.name,
      descriptionJson: data.descriptionJson,
      label: data.label,
      streamCategory: data.streamCategory,
      paidData:
        isPaid && data.paidData ? buildPaidDataInput(data.paidData) : undefined,
      packageOnly,
      apiAccessEnabled: data.apiAccessEnabled,
      streamType: data.streamType,
      customTargetsData:
        customStream && data.customTargetsData
          ? data.customTargetsData
          : undefined,
    };

    await onSubmit(input);
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(submit)}>{children}</form>
    </FormProvider>
  );
};
